import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Smile } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Self-contained emoji picker — no external lib (prod CSP forbids third-party
 * assets and we don't want to ship a heavy emoji bundle). A curated set of the
 * emojis that actually come up in outreach replies, grouped loosely. Clicking
 * one calls `onPick`; the popover stays open so several can be added in a row.
 */
const GROUPS: { key: string; emojis: string[] }[] = [
  {
    key: "faces",
    emojis: [
      "😀", "😁", "😂", "🤣", "😊", "😇", "🙂", "😉", "😍", "😘",
      "😎", "🤩", "🥳", "🤗", "🤔", "🙃", "😅", "😏", "😴", "😢",
      "😭", "😤", "😡", "🥺", "😱", "🤯", "😬", "🙄", "😶", "🤐",
    ],
  },
  {
    key: "gestures",
    emojis: [
      "👍", "👎", "👌", "🤝", "🙏", "👏", "🙌", "💪", "👋", "🤙",
      "✌️", "🤞", "👇", "👆", "👉", "👈", "✍️", "🫶", "🤲", "💁",
    ],
  },
  {
    key: "hearts",
    emojis: [
      "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "💯",
      "✨", "⭐", "🔥", "🎉", "🎊", "🎁", "💎", "🏆", "✅", "❌",
    ],
  },
  {
    key: "objects",
    emojis: [
      "💬", "📩", "📨", "📞", "📱", "💻", "🔗", "📎", "📌", "📅",
      "⏰", "💰", "💸", "🤑", "🛒", "🚀", "👀", "💡", "⚡", "❓",
    ],
  },
];

export function EmojiPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        aria-label={t("inbox.emoji")}
        title={t("inbox.emoji") ?? ""}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <Smile size={18} strokeWidth={2} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {GROUPS.map((g) => (
            <div key={g.key}>
              <p className="px-1 pb-1 text-xs font-medium text-fg-muted">
                {t(`inbox.emoji_group.${g.key}`)}
              </p>
              <div className="grid grid-cols-8 gap-0.5">
                {g.emojis.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => onPick(e)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-lg leading-none transition-colors hover:bg-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
EmojiPicker.displayName = "EmojiPicker";
